"use client";

import React, { useState, useEffect } from "react";
import classNames from "classnames/bind";
import { findDuplicateEmails } from "./findDuplicateEmails";
import { Tag } from "./Tag";
import styles from "./TagInput.module.sass";

interface TagsInputProps {
  id?: string;
  tags: { value: string; isValid: boolean }[];
  setTags: (tags: { value: string; isValid: boolean }[]) => void;
  setNumberOfDuplicated?: (counter: number) => void;
  placeholder?: string;
  customClass?: string;
  hasErrors?: boolean;
}

export const TagsInput = (props: TagsInputProps) => {
  const { id, tags, setTags, setNumberOfDuplicated, placeholder, customClass = "", hasErrors } = props;
  const [inputValue, setInputValue] = useState("");

  const cx = classNames.bind(styles);

  useEffect(() => {
    if (inputValue === "") {
      printDuplicateError(tags);
    }
  }, [inputValue]);

  const isValidEmail = (value: string) => {
    const emailRegex = /^[a-zA-Z0-9._+'-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,63}$/;
    return emailRegex.test(value);
  };

  const printDuplicateError = (tags) => {
    const { newTagsWithDuplicate, counter } = findDuplicateEmails(tags);
    setTags(newTagsWithDuplicate);
    setNumberOfDuplicated && setNumberOfDuplicated(counter);
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleInputKeyPress = (e) => {
    if (e.key === "Enter" || e.key === "," || e.key === ";" || e.key === " ") {
      e.preventDefault();
      const trimmedValue = inputValue.trim();
      if (trimmedValue !== "") {
        setTags([...tags, { value: trimmedValue, isValid: isValidEmail(trimmedValue) }]);
        setInputValue("");
      }
    }
  };

  const handleInputKeyDown = (e) => {
    if (e.key === "Backspace" && inputValue === "" && tags.length > 0) {
      const updatedTags = [...tags];
      updatedTags.pop();
      setTags(updatedTags);
      printDuplicateError(updatedTags);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const clipboardData = e.clipboardData || window.clipboardData;
    const pastedData = clipboardData.getData("Text");
    const newTags = pastedData.split(/[\t\n,;]/).filter((tag: string) => tag.trim() !== "");
    const validedTags = newTags.map((tag: string) => ({ value: tag.trim(), isValid: isValidEmail(tag.trim()) }));

    if (validedTags?.length > 0) {
      const newTags = [...tags, ...validedTags];
      setTags(newTags);
      printDuplicateError(newTags);
      setInputValue("");
    }
  };

  const handleTagRemove = (tagToRemove: number) => {
    const updatedTags = [...tags];
    updatedTags.splice(tagToRemove, 1);
    setTags(updatedTags);
    printDuplicateError(updatedTags);
  };

  const tagInputStyles = cx("TagInput", {
    "--error": hasErrors,
  });

  return (
    <div className={`${tagInputStyles} ${customClass}`}>
      <div className={styles.Tags}>
        {tags.map((tag, index) => (
          <Tag key={`Tag-${index}`} handleTagRemove={handleTagRemove} index={index} tag={tag} />
        ))}
      </div>
      <textarea
        id={id}
        placeholder={placeholder}
        value={inputValue}
        onChange={handleInputChange}
        onKeyPress={handleInputKeyPress}
        onKeyDown={handleInputKeyDown}
        onPaste={handlePaste}
        data-qa="tags-input"
      />
    </div>
  );
};
